import type { TFunction } from "i18next";

import type {
  IReportMenuItemTranslated,
  IReportTemplateItem,
  IReportTemplateItemTranslated,
} from "../../models";
import { isSearchTokensMatch } from "../table.helpers";

const translateReportMenuItems = (
  items: IReportTemplateItem[],
  t: TFunction<"translation", undefined>,
): IReportTemplateItemTranslated[] => {
  const translatedGroups: IReportTemplateItemTranslated[] = [];

  for (const groupItem of items || []) {
    const groupTitleTranslated = t(groupItem.translationKey);

    const subItemsTranslated: IReportMenuItemTranslated[] = [];

    for (const subItem of groupItem.reportItems || []) {
      const subTitleTranslated = subItem.translationKey
        ? t(subItem.translationKey)
        : subItem.title;
      const subItemTranslated: IReportMenuItemTranslated = {
        id: subItem.id,
        titleTranslated: subTitleTranslated,
        ownerCategory: subItem.ownerCategory,
      };
      subItemsTranslated.push(subItemTranslated);
    }

    const groupTranslated: IReportTemplateItemTranslated = {
      id: groupItem.reportTemplateId,
      titleTranslated: groupTitleTranslated,
      items: subItemsTranslated,
    };

    translatedGroups.push(groupTranslated);
  }

  return translatedGroups;
};

/** Keeps only the reports whose title, together with their group title, holds every search word. */
export const filterReportMenuGroupsBySearch = ({
  groups,
  searchText,
}: {
  groups: IReportTemplateItemTranslated[];
  searchText: string | undefined;
}): IReportTemplateItemTranslated[] => {
  if (!searchText?.trim()) {
    return groups;
  }

  return groups
    .map(group => {
      const groupFiltered: IReportTemplateItemTranslated = {
        ...group,
        items: group.items.filter(item =>
          isSearchTokensMatch({
            texts: [group.titleTranslated, item.titleTranslated],
            searchValue: searchText,
          }),
        ),
      };
      return groupFiltered;
    })
    .filter(group => group.items.length > 0);
};

export const filterAndTranslateReportMenuItemsData = ({
  itemsRaw,
  searchText,
  t,
}: {
  itemsRaw: IReportTemplateItem[];
  searchText: string | undefined;
  t: TFunction<"translation", undefined>;
}): IReportTemplateItemTranslated[] => {
  const groups = translateReportMenuItems(itemsRaw, t);

  return filterReportMenuGroupsBySearch({ groups, searchText });
};
